import React from "react";

/**
 * Minimal React error boundary used to contain rare unhandled render
 * exceptions (e.g., NaN/Infinity propagating through computed values on
 * mobile touch events). Keeps the rest of the page interactive instead of
 * the entire React tree unmounting to a white screen.
 *
 * Usage:
 *   <ErrorBoundary fallback="Calculator unavailable — please refresh.">
 *     <ROICalculator />
 *   </ErrorBoundary>
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Log to the browser console so the failure is still surfaced for
    // debugging while the user-facing UI stays clean.
    console.error("[ErrorBoundary] caught:", error, info?.componentStack);
  }

  reset = () => this.setState({ hasError: false });

  render() {
    if (!this.state.hasError) return this.props.children;
    const fallback =
      this.props.fallback ||
      "Something went wrong rendering this section.";
    return (
      <div
        data-testid="error-boundary-fallback"
        style={{
          padding: "20px 22px",
          background: "#0a0a0a",
          color: "#fff",
          border: "1px solid #2a2a2a",
          fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
          fontSize: 14,
          lineHeight: 1.5,
          margin: "16px 0",
        }}
      >
        <div style={{ marginBottom: 12, color: "#FF6B00", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase" }}>
          Something didn&apos;t load
        </div>
        <div>{fallback}</div>
        <button
          type="button"
          onClick={this.reset}
          data-testid="error-boundary-retry"
          style={{
            marginTop: 14,
            background: "#FF6B00",
            color: "#fff",
            border: 0,
            padding: "10px 16px",
            fontFamily: "'Bebas Neue', 'Archivo Black', system-ui, sans-serif",
            fontSize: 13,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
